import React, { useEffect, useRef, useState } from "react";
import { Camera, UploadSimple, ArrowsClockwise, X, Check, User } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PhotoCapture
 * - Mode "preview": muestra la foto actual o placeholder
 * - Mode "camera":  abre la cámara y permite hacer la foto
 * - Mode "review":  permite confirmar o rehacer
 *
 * value: dataURL actual (string | null)
 * onChange: (newDataUrl: string | null) => void
 */
export default function PhotoCapture({ value, onChange, testid = "photo" }) {
  const [mode, setMode] = useState("preview"); // preview | camera | review
  const [shot, setShot] = useState(null); // dataURL temporal antes de confirmar
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => stopCamera(), []);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 800 }, height: { ideal: 800 } },
        audio: false,
      });
      streamRef.current = stream;
      setMode("camera");
      // Wait next tick for video element
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 60);
    } catch (e) {
      toast.error("No se pudo abrir la cámara. Comprueba los permisos.");
    }
  };

  const cancelCamera = () => {
    stopCamera();
    setMode("preview");
    setShot(null);
  };

  const capture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const size = Math.min(video.videoWidth, video.videoHeight) || 600;
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = 600; // ratio 4:5, ideal para carné
    const ctx = canvas.getContext("2d");
    // Recortamos al centro respetando ratio
    const srcRatio = video.videoWidth / video.videoHeight;
    const dstRatio = canvas.width / canvas.height;
    let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
    if (srcRatio > dstRatio) {
      // src es más ancha: recortar lados
      sw = video.videoHeight * dstRatio;
      sx = (video.videoWidth - sw) / 2;
    } else {
      sh = video.videoWidth / dstRatio;
      sy = (video.videoHeight - sh) / 2;
    }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setShot(dataUrl);
    setMode("review");
    stopCamera();
  };

  const retake = () => {
    setShot(null);
    openCamera();
  };

  const confirm = () => {
    onChange(shot);
    setShot(null);
    setMode("preview");
    toast.success("Foto añadida");
  };

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("La foto no debe superar 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      toast.success("Foto añadida");
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    if (!window.confirm("¿Eliminar la foto del socio?")) return;
    onChange(null);
  };

  return (
    <div className="bg-slate-50 border border-slate-200 p-3 flex items-center gap-4" style={{ borderRadius: 8 }} data-testid={testid}>
      {/* Photo box */}
      <div
        className="relative shrink-0 w-24 h-28 bg-white border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden"
        style={{ borderRadius: 8 }}
      >
        {mode === "camera" ? (
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
        ) : mode === "review" ? (
          <img src={shot} alt="" className="w-full h-full object-cover" />
        ) : value ? (
          <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-img`} />
        ) : (
          <User size={36} className="text-slate-300" weight="bold" />
        )}
      </div>

      {/* Controls */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Foto del socio</div>

        {mode === "preview" && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openCamera}
              data-testid={`${testid}-open-camera`}
              className="px-3 h-9 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition active:scale-[0.97]"
              style={{ borderRadius: 8 }}
            >
              <Camera size={14} weight="bold" /> Hacer foto
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`${testid}-upload`}
              className="px-3 h-9 border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition"
              style={{ borderRadius: 8 }}
            >
              <UploadSimple size={14} weight="bold" /> Subir archivo
            </button>
            {value && (
              <button
                type="button"
                onClick={removePhoto}
                data-testid={`${testid}-remove`}
                className="px-3 h-9 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition"
                style={{ borderRadius: 8 }}
              >
                <X size={14} weight="bold" /> Quitar
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </div>
        )}

        {mode === "camera" && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={capture}
              data-testid={`${testid}-capture`}
              className="px-3 h-9 bg-[#22C55E] hover:bg-[#16a34a] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              style={{ borderRadius: 8 }}
            >
              <Camera size={14} weight="fill" /> Capturar
            </button>
            <button
              type="button"
              onClick={cancelCamera}
              className="px-3 h-9 border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              style={{ borderRadius: 8 }}
            >
              <X size={14} /> Cancelar
            </button>
          </div>
        )}

        {mode === "review" && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirm}
              data-testid={`${testid}-confirm`}
              className="px-3 h-9 bg-[#22C55E] hover:bg-[#16a34a] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              style={{ borderRadius: 8 }}
            >
              <Check size={14} weight="bold" /> Usar esta foto
            </button>
            <button
              type="button"
              onClick={retake}
              className="px-3 h-9 border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              style={{ borderRadius: 8 }}
            >
              <ArrowsClockwise size={14} /> Repetir
            </button>
          </div>
        )}

        {mode === "preview" && !value && (
          <p className="text-[10px] text-slate-500 mt-1.5">Aparecerá en el carné digital del socio.</p>
        )}
      </div>
    </div>
  );
}
